import { useState, useEffect } from "react";
import { fetchVerifiedDeliveries } from "../services/shippingApi";
import { BadgeCheck, Truck, Clock, Loader2 } from "lucide-react";

const SHIP_MODE_LABELS = {
  AIR_PRIORITY: "FedEx Priority",
  AIR_ECONOMY: "FedEx Economy",
  SEA_LCL: "Sea Freight (LCL)",
  SEA_FCL: "Sea Freight (FCL)",
  COURIER: "Courier",
};

function matchPreset(productName, productCategory, presets) {
  if (!productName || !presets?.length) return null;
  const lower = productName.toLowerCase();
  const catLower = (productCategory || "").toLowerCase();

  for (const p of presets) {
    const nameTokens = p.name.toLowerCase().split(/\s+/);
    const matched = nameTokens.filter((t) => t.length > 3 && lower.includes(t));
    if (matched.length >= 2) return p;
  }

  for (const p of presets) {
    for (const tag of p.tags || []) {
      if (catLower.includes(tag) || lower.includes(tag)) return p;
    }
  }

  return null;
}

export default function VerifiedDeliveryBadge({
  productName = "",
  productCategory = "",
  className = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedDeliveries()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-xs text-slate-400 ${className}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading delivery records...
      </div>
    );
  }

  if (!data) return null;

  const preset = matchPreset(productName, productCategory, data.verified);

  if (!preset || preset.verified_deliveries === 0) {
    return (
      <div className={`flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 ${className}`}>
        <BadgeCheck className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">
            {data.total_deliveries}+ Verified Deliveries to Barbados
          </p>
          <p className="text-xs text-emerald-600">
            CheepSweepz has successfully cleared and delivered industrial imports via DHC HQ.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-emerald-100 flex-shrink-0">
          <BadgeCheck className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-emerald-900">
            Verified Delivery Record
          </p>
          <p className="text-xs text-emerald-700 mt-0.5">
            CheepSweepz has successfully delivered{" "}
            <span className="font-bold">{preset.verified_deliveries} units</span>{" "}
            of this product type to Barbados.
          </p>

          <div className="flex flex-wrap gap-3 mt-3">
            <span className="inline-flex items-center gap-1.5 text-xs bg-white/80 px-2.5 py-1 rounded-full text-emerald-700 font-medium">
              <Truck className="h-3.5 w-3.5" />
              {SHIP_MODE_LABELS[preset.default_ship_mode] || preset.default_ship_mode}
            </span>
            {preset.avg_landed_days > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs bg-white/80 px-2.5 py-1 rounded-full text-emerald-700 font-medium">
                <Clock className="h-3.5 w-3.5" />
                ~{preset.avg_landed_days} days to door
              </span>
            )}
          </div>

          {preset.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {preset.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
